import React from "react"
import * as Sentry from "@sentry/react"
import { useTranslations } from "./Localization"

export class BadRequest extends Error { }
export class NotAuthenticated extends Error { }
export class NotAuthorized extends Error { }
export class NotFound extends Error { }
export class SessionExpired extends Error { }
export function ThrowNotFound() {
    throw new NotFound()
}

function ErrorPage({ error }) {
    try {
        const t = useTranslations()
        let title = t("An unknown error happened")
        if (error instanceof SessionExpired) title = t("Session expired")
        if (error instanceof NotAuthenticated) title = t("Not authenticated")
        if (error instanceof NotAuthorized) title = t("Insufficient permission")
        if (error instanceof NotFound) title = t("Page not found")

        if (error instanceof SessionExpired || error instanceof NotAuthenticated) window.localStorage.clear()
        if (error instanceof NotAuthenticated) window.location.replace('/')

        return <div className="min-w-screen min-h-screen flex flex-col justify-center items-center">
            <h1 className="text-2xl">{title}</h1>
            <button className="mt-10 px-5 py-2 rounded-lg bg-blue-500 text-white" onClick={() => window.location = '/'}>{t("Go back to homepage")}</button>
        </div>
    } catch (err) {
        return <div className="min-w-screen min-h-screen flex flex-col justify-center items-center">
            <h1 className="text-2xl">An unknown error happened</h1>
        </div>
    }
}

const isSentryConfigured = process.env.REACT_APP_SENTRY_DSN && process.env.REACT_APP_SENTRY_ENV
if (isSentryConfigured) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.REACT_APP_SENTRY_ENV,
        defaultIntegrations: false
    })
}

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { error: null }
        this.setError = error => this.setState({ ...this.state, error })
        this.reportError = error => {
            if (isSentryConfigured
                && !(error instanceof NotAuthenticated)
                && !(error instanceof NotFound)
                && !error.message === 'Failed to fetch'
            ) Sentry.captureException(error)
        }
        this.handleGlobalError = event => {
            this.setError(event.error)
            this.reportError(event.error)
        }
        this.handlePromiseRejection = event => {
            this.setError(event.reason)
            this.reportError(event.reason)
        }

    }
    componentDidCatch(error, info) {
        this.setError(error)
        this.reportError(error)
    }
    componentDidMount() {
        window.addEventListener("error", this.handleGlobalError)
        window.addEventListener("unhandledrejection", this.handlePromiseRejection)
    }
    componentWillUnmount() {
        window.removeEventListener("error", this.handleGlobalError)
        window.removeEventListener("unhandledrejection", this.handlePromiseRejection)
    }
    render() {
        if (this.state.error) return <ErrorPage error={this.state.error} setError={this.setError} />
        return this.props.children
    }
}